import { RowPlayToggle } from '@src/components/common/RowPlayToggle';
import SpeakerMomentPreview from '@src/components/sidepanel/modals/SpeakerMomentPreview';
import { isTurnPlayable } from '@src/utility/frameExtraction';
import React from 'react';
import styled from 'styled-components';

import type { SpeakerTurnRange } from '@src/utility/frameExtraction';

/**
 * A speaker's row tile: their frame, with the moment they start talking behind
 * the same reveal-on-hover play/pause the Reel Editor and Add Clips rows use.
 *
 * The player is mounted by the playing row and by no other, so starting a
 * second row stops the first by construction and a story with a dozen speakers
 * costs one media element rather than twelve.
 *
 * Falls back to a bare still when the transcript gave no timestamp or the story
 * has no playable source, and to an empty tile when the frame itself never
 * arrived — the row keeps its column either way.
 */
const SpeakerMomentCell: React.FC<{
  frameUrl?: string;
  speakerName: string;
  /** The turn this row shows and plays. Absent when the transcript gave none. */
  turn?: SpeakerTurnRange;
  mp4Url?: string;
  muxPlaybackId?: string;
  /** This is the row holding the player — playing, or paused where it stopped. */
  isActive: boolean;
  isPlaying: boolean;
  onToggle: () => void;
  onEnded: () => void;
}> = ({
  frameUrl,
  speakerName,
  turn,
  mp4Url,
  muxPlaybackId,
  isActive,
  isPlaying,
  onToggle,
  onEnded,
}) => {
  const canPlay = isTurnPlayable(turn) && (mp4Url || muxPlaybackId);

  return (
    <MomentTile>
      {canPlay && isActive ? (
        <SpeakerMomentPreview
          mp4Url={mp4Url}
          muxPlaybackId={muxPlaybackId}
          startTime={turn.start}
          endTime={turn.end}
          poster={frameUrl}
          label={speakerName}
          isPlaying={isPlaying}
          onEnded={onEnded}
        />
      ) : (
        frameUrl && (
          <MomentStill src={frameUrl} alt={`${speakerName} on camera`} />
        )
      )}
      {canPlay && (
        <RowPlayToggle
          isPlaying={isActive && isPlaying}
          playLabel={`Play ${speakerName}`}
          onToggle={onToggle}
          // Seen once, by someone confirming names: the frame must say it
          // plays without being hovered first.
          alwaysVisible
        />
      )}
    </MomentTile>
  );
};

const MomentTile = styled.div`
  position: relative;
  width: 96px;
  aspect-ratio: 16 / 9;
  flex-shrink: 0;
  border-radius: var(--radius-input);
  background-color: var(--surface-elevated);

  &:hover [data-play-toggle] {
    opacity: 1;
  }
`;

const MomentStill = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius-input);
`;

export default SpeakerMomentCell;
